import { useState } from 'react';
import { Landmark, CheckCircle2, ChevronLeft, ChevronRight, Upload } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { createAgentLoan, createLoan, deleteLoan, uploadAgentLoanDocument, uploadLoanDocument } from '../../../lib/api';
import { getLoanApplicationRoleConfig } from '../../../lib/loanApplicationFlow';
import { buildLoanApplicationPayload, validateLoanApplicationStep, validateLoanApplicationSubmission } from '../../../lib/loanApplicationValidation';
import { toast } from 'sonner';

function Field({ label, required, children }) {
    return (
        <div className="flex flex-col gap-1.5">
            <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {label}{required ? <span className="text-rose-500 ml-0.5">*</span> : null}
            </Label>
            {children}
        </div>
    );
}

function FormInput({ id, type, label, placeholder, value, onChange, required }) {
    return (
        <Field label={label} required={required}>
            <Input id={id} type={type || 'text'} placeholder={placeholder} value={value} onChange={onChange} required={required}
                className="h-11 rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all" />
        </Field>
    );
}

function FormSelect({ id, label, options, value, onChange, required }) {
    return (
        <Field label={label} required={required}>
            <select id={id} value={value} onChange={onChange} required={required}
                className="h-11 rounded-xl border px-3 text-sm w-full outline-none border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all">
                <option value="">Select…</option>
                {options.map(function (o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
            </select>
        </Field>
    );
}

function FileUploadField({ label, accept, onChange, file }) {
    return (
        <div className="flex flex-col gap-1.5">
            <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</Label>
            <label className="flex flex-col items-center justify-center gap-2 h-24 border-2 border-dashed rounded-xl cursor-pointer border-slate-200 dark:border-slate-700 hover:border-violet-400 bg-slate-50 dark:bg-slate-800/50 hover:bg-violet-50/50 transition-all group">
                <input type="file" accept={accept} className="sr-only" onChange={onChange} />
                {file ? (
                    <div className="flex items-center gap-2 px-3"><CheckCircle2 className="h-5 w-5 text-violet-500 shrink-0" /><span className="text-xs text-violet-600 font-medium truncate max-w-[160px]">{file.name}</span></div>
                ) : (
                    <div className="flex flex-col items-center gap-1"><Upload className="h-5 w-5 text-slate-400 group-hover:text-violet-500 transition-colors" /><span className="text-xs text-slate-500">Click to upload</span></div>
                )}
            </label>
        </div>
    );
}

function ProgressBar({ steps, current }) {
    var pct = steps.length > 1 ? Math.round((current / (steps.length - 1)) * 100) : 100;
    return (
        <div className="w-full">
            <div className="flex items-start justify-between mb-6">
                {steps.map(function (step, i) {
                    var done = i < current, active = i === current;
                    var cc = done ? 'bg-violet-500 border-violet-500 text-white' : active ? 'bg-white dark:bg-slate-900 border-violet-500 text-violet-600' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-400';
                    var lc = active ? 'text-violet-600 dark:text-violet-400' : done ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400';
                    return (
                        <div key={i} className="flex-1 flex flex-col items-center relative">
                            {i < steps.length - 1 ? <div className={'absolute top-4 left-1/2 w-full h-0.5 ' + (done ? 'bg-violet-500' : 'bg-slate-200 dark:bg-slate-700')} /> : null}
                            <div className={'relative z-10 h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold border-2 ' + cc}>{done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}</div>
                            <span className={'mt-2 text-[10px] font-medium text-center leading-tight max-w-[70px] ' + lc}>{step}</span>
                        </div>
                    );
                })}
            </div>
            <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all duration-700" style={{ width: pct + '%' }} />
            </div>
            <div className="flex justify-between mt-1">
                <span className="text-[10px] text-slate-400">Step {current + 1} of {steps.length}</span>
                <span className="text-[10px] text-violet-600 font-semibold">{pct}% Complete</span>
            </div>
        </div>
    );
}

var STEPS = ['Personal Details', 'Documents', 'Review & Submit'];

const AgentPersonalLoan = ({ roleBasePath = '/agent' }) => {
    const roleConfig = getLoanApplicationRoleConfig(roleBasePath);
    const isAgentPortal = roleBasePath === '/agent';
    const createLoanRequest = isAgentPortal ? createAgentLoan : createLoan;
    const uploadLoanDocumentRequest = isAgentPortal ? uploadAgentLoanDocument : uploadLoanDocument;
    var sd = useState({ fullName: '', mobile: '', email: '', pan: '', aadhaar: '', employmentType: '', companyName: '', monthlyIncome: '', loanAmount: '', loanPurpose: '' });
    var data = sd[0]; var setData = sd[1];
    var sf = useState({}); var files = sf[0]; var setFiles = sf[1];
    var sc = useState(0); var current = sc[0]; var setCurrent = sc[1];
    var ss = useState(false); var submitted = ss[0]; var setSubmitted = ss[1];
    const { token } = useAuth();

    function upd(k, v) { setData(function (p) { var n = Object.assign({}, p); n[k] = v; return n; }); }
    function updFile(k, f) { setFiles(function (p) { var n = Object.assign({}, p); n[k] = f; return n; }); }
    function c(k) { return { value: data[k], onChange: function (e) { upd(k, e.target.value); } }; }

    var isLast = current === STEPS.length - 1;
    async function handleNext() {
        var state = { data: data, files: files };
        if (!isLast) {
            var stepErrors = validateLoanApplicationStep('personal', current, state);
            if (Object.keys(stepErrors).length > 0) {
                toast.error(Object.values(stepErrors)[0] || 'Please fix the highlighted fields.');
                return;
            }
        }
        if (isLast) {
            if (!token) {
                toast.error('Please log in to file this loan application.');
                return;
            }
            var validation = validateLoanApplicationSubmission('personal', STEPS.length, state);
            if (!validation.success) {
                setCurrent(validation.stepIndex);
                toast.error(Object.values(validation.errors)[0] || 'Please fix the highlighted fields.');
                return;
            }
            let createdLoanId = null;
            try {
                const resp = await createLoanRequest(token, buildLoanApplicationPayload('personal', state));
                const loanId = resp?.data?.id;
                createdLoanId = loanId;
                const fileEntries = Object.entries(files || {}).filter(([, f]) => f instanceof File);
                if (loanId && fileEntries.length > 0) {
                    toast.info(`Uploading ${fileEntries.length} document(s)…`);
                    const uploadResults = await Promise.allSettled(
                        fileEntries.map(([key, file]) => uploadLoanDocumentRequest(token, loanId, key, file))
                    );
                    const failedDocKeys = uploadResults.reduce((acc, result, index) => {
                        if (result.status === 'rejected') acc.push(fileEntries[index][0]);
                        return acc;
                    }, []);
                    if (failedDocKeys.length > 0) {
                        const firstFailure = uploadResults.find((result) => result.status === 'rejected');
                        const failureReason = firstFailure?.status === 'rejected' ? firstFailure.reason?.message : '';
                        throw new Error(
                            `Document upload failed for: ${failedDocKeys.join(', ')}${failureReason ? `. ${failureReason}` : ''}`
                        );
                    }
                }
                toast.success('Personal loan application filed successfully.');
                setSubmitted(true);
            } catch (err) {
                let message = err?.message || 'Failed to file personal loan application';
                if (createdLoanId) {
                    try {
                        await deleteLoan(token, createdLoanId, { rollbackUploadFailure: true });
                        message = `${message}. Created loan was rolled back.`;
                    } catch (rollbackError) {
                        const rollbackMessage = rollbackError?.message || 'Failed to roll back the created loan';
                        message = `${message}. Rollback failed: ${rollbackMessage}`;
                    }
                }
                toast.error(message);
            }
            return;
        }
        setCurrent(function (p) { return p + 1; });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    function handleBack() { setCurrent(function (p) { return p - 1; }); window.scrollTo({ top: 0, behavior: 'smooth' }); }

    var docs = [{ key: 'panCard', label: 'PAN Card', accept: '.pdf,.jpg,.jpeg,.png' }, { key: 'aadhaarCard', label: 'Aadhaar Card', accept: '.pdf,.jpg,.jpeg,.png' }, { key: 'salarySlips', label: 'Salary Slips (Last 3 Months)', accept: '.pdf' }, { key: 'bankStatement', label: 'Bank Statement (6 Months)', accept: '.pdf' }, { key: 'photograph', label: 'Photograph', accept: '.jpg,.jpeg,.png' }];

    var stepContent;
    if (current === 0) {
        stepContent = (
            <div className="space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                    <FormInput id="fn" label="Full Name" placeholder="Priya Sharma" required {...c('fullName')} />
                    <FormInput id="mo" label="Mobile Number" type="tel" placeholder="+91 98765 43210" required {...c('mobile')} />
                    <FormInput id="em" label="Email" type="email" placeholder="priya@example.com" required {...c('email')} />
                    <FormInput id="pa" label="PAN Number" placeholder="ABCDE1234F" required {...c('pan')} />
                    <FormInput id="aa" label="Aadhaar Number" placeholder="1234 5678 9012" required {...c('aadhaar')} />
                    <FormSelect id="et" label="Employment Type" required options={[{ value: 'salaried', label: 'Salaried' }, { value: 'self_employed', label: 'Self Employed' }]} {...c('employmentType')} />
                    <FormInput id="cn" label="Company Name" placeholder="TCS Ltd." required {...c('companyName')} />
                    <FormInput id="mi" label="Monthly Income (Rs.)" type="number" placeholder="60000" required {...c('monthlyIncome')} />
                    <FormInput id="la" label="Loan Amount Required (Rs.)" type="number" placeholder="500000" required {...c('loanAmount')} />
                </div>
                <div className="flex flex-col gap-1.5 sm:col-span-2">
                    <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Loan Purpose</Label>
                    <Textarea id="lp" placeholder="Describe the purpose of the loan..." rows={3} value={data.loanPurpose} onChange={function (e) { upd('loanPurpose', e.target.value); }} className="resize-none rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800" />
                </div>
            </div>
        );
    } else if (current === 1) {
        stepContent = (<div className="grid sm:grid-cols-2 gap-4">{docs.map(function (d) { return <FileUploadField key={d.key} label={d.label} accept={d.accept} file={files[d.key]} onChange={function (e) { updFile(d.key, e.target.files[0]); }} />; })}</div>);
    } else {
        var rows = [['Full Name', data.fullName], ['Mobile', data.mobile], ['Email', data.email], ['PAN', data.pan], ['Employment', data.employmentType], ['Company', data.companyName], ['Monthly Income', data.monthlyIncome ? 'Rs. ' + Number(data.monthlyIncome).toLocaleString('en-IN') : '-'], ['Loan Amount', data.loanAmount ? 'Rs. ' + Number(data.loanAmount).toLocaleString('en-IN') : '-'], ['Loan Purpose', data.loanPurpose || '-']];
        stepContent = (<div className="space-y-4"><p className="text-sm text-slate-500 dark:text-slate-400">Review before submitting.</p><div className="rounded-xl border border-slate-100 dark:border-slate-800 overflow-hidden"><table className="w-full text-sm"><tbody>{rows.map(function (row, i) { return (<tr key={row[0]} className={i % 2 === 0 ? 'bg-slate-50 dark:bg-slate-800/40' : 'bg-white dark:bg-slate-900'}><td className="px-4 py-2.5 font-medium text-slate-600 dark:text-slate-400 w-1/2">{row[0]}</td><td className="px-4 py-2.5 text-slate-900 dark:text-slate-100">{row[1] || '-'}</td></tr>); })}</tbody></table></div><p className="text-xs text-slate-400">By submitting you agree to our Terms and Privacy Policy.</p></div>);
    }

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 md:p-8">
            <div className="max-w-4xl mx-auto space-y-6">
                <div className="flex items-center gap-4 mb-2">
                    <div className="h-10 w-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
                        <Landmark className="h-5 w-5 text-violet-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-['Plus_Jakarta_Sans']">Personal Loan Application</h1>
                        <p className="text-sm text-slate-500">{roleConfig.portalLabel}</p>
                    </div>
                </div>

                {submitted ? (
                    <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-8 md:p-12 text-center space-y-5 shadow-sm">
                        <div className="h-24 w-24 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center animate-bounce mx-auto">
                            <CheckCircle2 className="h-12 w-12 text-violet-500" />
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white font-['Plus_Jakarta_Sans']">Application Filed Successfully!</h2>
                        <p className="text-slate-600 dark:text-slate-400 max-w-sm mx-auto">The <strong>Personal Loan</strong> application has been recorded. You can track its status in your dashboard.</p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                            <Button asChild className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl px-8"><Link to={roleConfig.loansPath}>View All Loans</Link></Button>
                            <Button variant="outline" asChild className="rounded-xl px-8"><Link to={roleConfig.dashboardPath}>Dashboard</Link></Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-6">
                        <ProgressBar steps={STEPS} current={current} />
                        <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-6 md:p-8 space-y-6">
                            <div className="border-b border-slate-100 dark:border-slate-800 pb-4">
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white font-['Plus_Jakarta_Sans']">{STEPS[current]}</h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Fields marked <span className="text-rose-500">*</span> are required.</p>
                            </div>
                            {stepContent}
                            <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800">
                                <Button variant="outline" onClick={handleBack} disabled={current === 0} className="rounded-xl gap-1.5"><ChevronLeft className="h-4 w-4" />Back</Button>
                                <Button onClick={handleNext} className="rounded-xl gap-1.5 bg-slate-900 hover:bg-slate-800 dark:bg-violet-600 dark:hover:bg-violet-700 text-white px-6">
                                    {isLast ? 'Submit Application' : 'Continue'}
                                    {isLast ? <CheckCircle2 className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AgentPersonalLoan;
